import { useCallback } from "react";

import { DonationFormCategory, OrganizationCategory } from "@simplyk/common";

import { useCurrentUserContext } from "../contexts/CurrentUserContext";

import { Translate } from "@/common/hooks/useTranslate";

type SupportedCategory =
  | OrganizationCategory.AnimalWelfare
  | OrganizationCategory.CommunityService
  | OrganizationCategory.Culture
  | OrganizationCategory.Education
  | OrganizationCategory.Environment
  | OrganizationCategory.Health
  | OrganizationCategory.Humanitarian
  | OrganizationCategory.Other
  | OrganizationCategory.Politic
  | OrganizationCategory.Religion
  | OrganizationCategory.SocialService
  | OrganizationCategory.SportsAndLeisure
  | OrganizationCategory.StudentGroup
  | OrganizationCategory.Veterans;

const SUPPORTED_CATEGORIES: SupportedCategory[] = [
  OrganizationCategory.AnimalWelfare,
  OrganizationCategory.CommunityService,
  OrganizationCategory.Culture,
  OrganizationCategory.Education,
  OrganizationCategory.Environment,
  OrganizationCategory.Health,
  OrganizationCategory.Humanitarian,
  OrganizationCategory.Other,
  OrganizationCategory.Politic,
  OrganizationCategory.Religion,
  OrganizationCategory.SocialService,
  OrganizationCategory.SportsAndLeisure,
  OrganizationCategory.StudentGroup,
  OrganizationCategory.Veterans,
];

const isSupportedCategory = (category: OrganizationCategory): category is SupportedCategory =>
  (SUPPORTED_CATEGORIES as OrganizationCategory[]).includes(category);

const crossSellTitleKeys = {
  [OrganizationCategory.AnimalWelfare]: {
    [DonationFormCategory.DonationForm]: "crossSell.AnimalWelfare.DonationForm.title",
    [DonationFormCategory.PeerToPeerCampaign]: "crossSell.AnimalWelfare.PeerToPeerCampaign.title",
  },
  [OrganizationCategory.CommunityService]: {
    [DonationFormCategory.DonationForm]: "crossSell.CommunityService.DonationForm.title",
    [DonationFormCategory.PeerToPeerCampaign]: "crossSell.CommunityService.PeerToPeerCampaign.title",
  },
  [OrganizationCategory.Culture]: {
    [DonationFormCategory.DonationForm]: "crossSell.Culture.DonationForm.title",
    [DonationFormCategory.PeerToPeerCampaign]: "crossSell.Culture.PeerToPeerCampaign.title",
  },
  [OrganizationCategory.Education]: {
    [DonationFormCategory.DonationForm]: "crossSell.Education.DonationForm.title",
    [DonationFormCategory.PeerToPeerCampaign]: "crossSell.Education.PeerToPeerCampaign.title",
  },
  [OrganizationCategory.Environment]: {
    [DonationFormCategory.DonationForm]: "crossSell.Environment.DonationForm.title",
    [DonationFormCategory.PeerToPeerCampaign]: "crossSell.Environment.PeerToPeerCampaign.title",
  },
  [OrganizationCategory.Health]: {
    [DonationFormCategory.DonationForm]: "crossSell.Health.DonationForm.title",
    [DonationFormCategory.PeerToPeerCampaign]: "crossSell.Health.PeerToPeerCampaign.title",
  },
  [OrganizationCategory.Humanitarian]: {
    [DonationFormCategory.DonationForm]: "crossSell.Humanitarian.DonationForm.title",
    [DonationFormCategory.PeerToPeerCampaign]: "crossSell.Humanitarian.PeerToPeerCampaign.title",
  },
  [OrganizationCategory.Other]: {
    [DonationFormCategory.DonationForm]: "crossSell.Other.DonationForm.title",
    [DonationFormCategory.PeerToPeerCampaign]: "crossSell.Other.PeerToPeerCampaign.title",
  },
  [OrganizationCategory.Politic]: {
    [DonationFormCategory.DonationForm]: "crossSell.Politic.DonationForm.title",
    [DonationFormCategory.PeerToPeerCampaign]: "crossSell.Politic.PeerToPeerCampaign.title",
  },
  [OrganizationCategory.Religion]: {
    [DonationFormCategory.DonationForm]: "crossSell.Religion.DonationForm.title",
    [DonationFormCategory.PeerToPeerCampaign]: "crossSell.Religion.PeerToPeerCampaign.title",
  },
  [OrganizationCategory.SocialService]: {
    [DonationFormCategory.DonationForm]: "crossSell.SocialService.DonationForm.title",
    [DonationFormCategory.PeerToPeerCampaign]: "crossSell.SocialService.PeerToPeerCampaign.title",
  },
  [OrganizationCategory.SportsAndLeisure]: {
    [DonationFormCategory.DonationForm]: "crossSell.SportsAndLeisure.DonationForm.title",
    [DonationFormCategory.PeerToPeerCampaign]: "crossSell.SportsAndLeisure.PeerToPeerCampaign.title",
  },
  [OrganizationCategory.StudentGroup]: {
    [DonationFormCategory.DonationForm]: "crossSell.StudentGroup.DonationForm.title",
    [DonationFormCategory.PeerToPeerCampaign]: "crossSell.StudentGroup.PeerToPeerCampaign.title",
  },
  [OrganizationCategory.Veterans]: {
    [DonationFormCategory.DonationForm]: "crossSell.Veterans.DonationForm.title",
    [DonationFormCategory.PeerToPeerCampaign]: "crossSell.Veterans.PeerToPeerCampaign.title",
  },
} as const;

const crossSellDescriptionKeys = {
  [OrganizationCategory.AnimalWelfare]: {
    [DonationFormCategory.DonationForm]: "crossSell.AnimalWelfare.DonationForm.description",
    [DonationFormCategory.PeerToPeerCampaign]: "crossSell.AnimalWelfare.PeerToPeerCampaign.description",
  },
  [OrganizationCategory.CommunityService]: {
    [DonationFormCategory.DonationForm]: "crossSell.CommunityService.DonationForm.description",
    [DonationFormCategory.PeerToPeerCampaign]: "crossSell.CommunityService.PeerToPeerCampaign.description",
  },
  [OrganizationCategory.Culture]: {
    [DonationFormCategory.DonationForm]: "crossSell.Culture.DonationForm.description",
    [DonationFormCategory.PeerToPeerCampaign]: "crossSell.Culture.PeerToPeerCampaign.description",
  },
  [OrganizationCategory.Education]: {
    [DonationFormCategory.DonationForm]: "crossSell.Education.DonationForm.description",
    [DonationFormCategory.PeerToPeerCampaign]: "crossSell.Education.PeerToPeerCampaign.description",
  },
  [OrganizationCategory.Environment]: {
    [DonationFormCategory.DonationForm]: "crossSell.Environment.DonationForm.description",
    [DonationFormCategory.PeerToPeerCampaign]: "crossSell.Environment.PeerToPeerCampaign.description",
  },
  [OrganizationCategory.Health]: {
    [DonationFormCategory.DonationForm]: "crossSell.Health.DonationForm.description",
    [DonationFormCategory.PeerToPeerCampaign]: "crossSell.Health.PeerToPeerCampaign.description",
  },
  [OrganizationCategory.Humanitarian]: {
    [DonationFormCategory.DonationForm]: "crossSell.Humanitarian.DonationForm.description",
    [DonationFormCategory.PeerToPeerCampaign]: "crossSell.Humanitarian.PeerToPeerCampaign.description",
  },
  [OrganizationCategory.Other]: {
    [DonationFormCategory.DonationForm]: "crossSell.Other.DonationForm.description",
    [DonationFormCategory.PeerToPeerCampaign]: "crossSell.Other.PeerToPeerCampaign.description",
  },
  [OrganizationCategory.Politic]: {
    [DonationFormCategory.DonationForm]: "crossSell.Politic.DonationForm.description",
    [DonationFormCategory.PeerToPeerCampaign]: "crossSell.Politic.PeerToPeerCampaign.description",
  },
  [OrganizationCategory.Religion]: {
    [DonationFormCategory.DonationForm]: "crossSell.Religion.DonationForm.description",
    [DonationFormCategory.PeerToPeerCampaign]: "crossSell.Religion.PeerToPeerCampaign.description",
  },
  [OrganizationCategory.SocialService]: {
    [DonationFormCategory.DonationForm]: "crossSell.SocialService.DonationForm.description",
    [DonationFormCategory.PeerToPeerCampaign]: "crossSell.SocialService.PeerToPeerCampaign.description",
  },
  [OrganizationCategory.SportsAndLeisure]: {
    [DonationFormCategory.DonationForm]: "crossSell.SportsAndLeisure.DonationForm.description",
    [DonationFormCategory.PeerToPeerCampaign]: "crossSell.SportsAndLeisure.PeerToPeerCampaign.description",
  },
  [OrganizationCategory.StudentGroup]: {
    [DonationFormCategory.DonationForm]: "crossSell.StudentGroup.DonationForm.description",
    [DonationFormCategory.PeerToPeerCampaign]: "crossSell.StudentGroup.PeerToPeerCampaign.description",
  },
  [OrganizationCategory.Veterans]: {
    [DonationFormCategory.DonationForm]: "crossSell.Veterans.DonationForm.description",
    [DonationFormCategory.PeerToPeerCampaign]: "crossSell.Veterans.PeerToPeerCampaign.description",
  },
} as const;

export const useGetCrossSellDonationFormInput = () => {
  const { organization } = useCurrentUserContext();
  const category = organization?.category;

  return useCallback(
    (formCategory: DonationFormCategory, t: Translate) => {
      if (!category || !isSupportedCategory(category)) {
        return {
          title: undefined,
          description: undefined,
        };
      }

      return {
        title: t("dashboard", crossSellTitleKeys[category][formCategory]),
        description: t("dashboard", crossSellDescriptionKeys[category][formCategory]),
      };
    },
    [category]
  );
};
